import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { type FileUploadState, type FileStatus } from "@/hooks/useFileUpload";
import { cn } from "@/lib/utils";
import {
  CheckCircle2Icon,
  CircleIcon,
  Loader2Icon,
  XCircleIcon,
  AlertCircleIcon,
  MinusIcon,
  Maximize2Icon,
  XIcon,
} from "lucide-react";

interface UploadProgressDialogProps {
  state: FileUploadState;
  files: File[];
  onCancel: () => void;
  onClose: () => void;
  isOpen: boolean;
  // Optional controlled minimized state (for global provider)
  isMinimized?: boolean;
  onMinimize?: () => void;
  onExpand?: () => void;
}

function FileStatusIcon({
  status,
  isUploading,
}: {
  status: FileStatus;
  isUploading: boolean;
}) {
  switch (status) {
    case "pending":
      // Show spinner for pending files while upload is in progress
      if (isUploading) {
        return (
          <Loader2Icon className="h-4 w-4 animate-spin text-muted-foreground" />
        );
      }
      return <CircleIcon className="h-4 w-4 text-muted-foreground" />;
    case "uploading":
      return <Loader2Icon className="h-4 w-4 animate-spin text-primary" />;
    case "completed":
      return <CheckCircle2Icon className="h-4 w-4 text-green-600" />;
    case "error":
      return <XCircleIcon className="h-4 w-4 text-destructive" />;
    default:
      return <CircleIcon className="h-4 w-4 text-muted-foreground" />;
  }
}

function getFileKey(file: File): string {
  return `${file.name}|${file.size}`;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Minimized floating indicator shown when upload is in progress but minimized
 */
function MinimizedUploadIndicator({
  state,
  onExpand,
  onClose,
}: {
  state: FileUploadState;
  onExpand: () => void;
  onClose: () => void;
}) {
  const isUploading = state.status === "uploading";
  const isCompleted = state.status === "completed";
  const isCancelled = state.status === "cancelled";
  const hasError = state.status === "error";

  return (
    <div
      className={cn(
        "fixed left-4 top-4 z-[60] flex items-center gap-3 rounded-lg border bg-background p-3 shadow-lg",
        "animate-in slide-in-from-top-2 duration-200",
        hasError && "border-destructive/50",
        isCompleted && "border-green-500/50",
      )}
    >
      {/* Status icon */}
      <div className="flex-shrink-0">
        {isUploading && (
          <Loader2Icon className="h-5 w-5 animate-spin text-primary" />
        )}
        {isCompleted && <CheckCircle2Icon className="h-5 w-5 text-green-600" />}
        {isCancelled && <AlertCircleIcon className="h-5 w-5 text-yellow-600" />}
        {hasError && <XCircleIcon className="h-5 w-5 text-destructive" />}
      </div>

      {/* Progress info - clickable to expand */}
      <button
        onClick={onExpand}
        className="flex flex-col gap-1 text-left transition-opacity hover:opacity-80"
      >
        <span className="text-sm font-medium">
          {isUploading && "Uploading..."}
          {isCompleted && "Upload Complete"}
          {isCancelled && "Upload Cancelled"}
          {hasError && "Upload Error"}
        </span>
        <div className="flex items-center gap-2">
          <Progress
            value={state.completedFiles}
            max={state.totalFiles}
            className={cn(
              "h-1.5 w-24",
              hasError && "bg-destructive/20",
              isCancelled && "bg-yellow-200",
            )}
            indicatorClassName={cn(
              hasError && "bg-destructive",
              isCancelled && "bg-yellow-500",
            )}
          />
          <span className="text-xs text-muted-foreground">
            {state.completedFiles}/{state.totalFiles}
          </span>
        </div>
      </button>

      {/* Action buttons */}
      <div className="ml-2 flex items-center gap-1">
        {/* Expand button */}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onExpand}
          title="Expand"
        >
          <Maximize2Icon className="h-4 w-4" />
        </Button>

        {/* Close button - only show when done */}
        {!isUploading && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={onClose}
            title="Close"
          >
            <XIcon className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

export function UploadProgressDialog({
  state,
  files,
  onCancel,
  onClose,
  isOpen,
  // Controlled props (optional)
  isMinimized: controlledIsMinimized,
  onMinimize,
  onExpand,
}: UploadProgressDialogProps) {
  // Internal state for uncontrolled usage
  const [internalIsMinimized, setInternalIsMinimized] = React.useState(false);

  // Use controlled or uncontrolled minimized state
  const isControlled = controlledIsMinimized !== undefined;
  const isMinimized = isControlled ? controlledIsMinimized : internalIsMinimized;

  const handleMinimize = React.useCallback(() => {
    if (isControlled && onMinimize) {
      onMinimize();
    } else {
      setInternalIsMinimized(true);
    }
  }, [isControlled, onMinimize]);

  const handleExpand = React.useCallback(() => {
    if (isControlled && onExpand) {
      onExpand();
    } else {
      setInternalIsMinimized(false);
    }
  }, [isControlled, onExpand]);

  const percentage =
    state.totalFiles > 0
      ? Math.round((state.completedFiles / state.totalFiles) * 100)
      : 0;

  const isUploading = state.status === "uploading";
  const isCompleted = state.status === "completed";
  const isCancelled = state.status === "cancelled";
  const hasError = state.status === "error";
  const isDone = isCompleted || isCancelled || hasError;

  // Reset internal minimized state when dialog closes (uncontrolled mode only)
  React.useEffect(() => {
    if (!isOpen && !isControlled) {
      setInternalIsMinimized(false);
    }
  }, [isOpen, isControlled]);

  const title = React.useMemo(() => {
    if (isUploading) return "Uploading Documents";
    if (isCompleted) return "Upload Complete";
    if (isCancelled) return "Upload Cancelled";
    if (hasError) return "Upload Error";
    return "Upload Documents";
  }, [isUploading, isCompleted, isCancelled, hasError]);

  const description = React.useMemo(() => {
    if (isUploading) {
      return `Uploading batch ${state.currentBatch} of ${state.totalBatches}...`;
    }
    if (isCompleted) {
      return `Successfully uploaded ${state.completedFiles} of ${state.totalFiles} files.`;
    }
    if (isCancelled) {
      return `Upload cancelled. ${state.completedFiles} of ${state.totalFiles} files were uploaded before cancellation.`;
    }
    if (hasError) {
      return state.error || "An error occurred during upload.";
    }
    return "";
  }, [state, isUploading, isCompleted, isCancelled, hasError]);

  // Show minimized indicator when minimized
  if (isOpen && isMinimized) {
    return (
      <MinimizedUploadIndicator
        state={state}
        onExpand={handleExpand}
        onClose={onClose}
      />
    );
  }

  return (
    <Dialog
      open={isOpen && !isMinimized}
      onOpenChange={(open) => !open && isDone && onClose()}
    >
      <DialogContent showCloseButton={isDone}>
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              {isUploading && <Loader2Icon className="h-5 w-5 animate-spin" />}
              {isCompleted && (
                <CheckCircle2Icon className="h-5 w-5 text-green-600" />
              )}
              {isCancelled && (
                <AlertCircleIcon className="h-5 w-5 text-yellow-600" />
              )}
              {hasError && <XCircleIcon className="h-5 w-5 text-destructive" />}
              {title}
            </DialogTitle>
            {/* Minimize button - only show while uploading */}
            {isUploading && (
              <Button
                variant="ghost"
                size="icon"
                className="-mr-2 h-8 w-8"
                onClick={handleMinimize}
                title="Minimize to continue using the app"
              >
                <MinusIcon className="h-4 w-4" />
              </Button>
            )}
          </div>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-hidden">
          {/* Progress bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>
                {state.completedFiles} of {state.totalFiles} files
              </span>
              <span>{percentage}%</span>
            </div>
            <Progress
              value={state.completedFiles}
              max={state.totalFiles}
              className={cn(
                hasError && "bg-destructive/20",
                isCancelled && "bg-yellow-200",
              )}
              indicatorClassName={cn(
                hasError && "bg-destructive",
                isCancelled && "bg-yellow-500",
              )}
            />
          </div>

          {/* File list */}
          <div className="h-48 overflow-x-hidden overflow-y-auto rounded-md border">
            <div className="w-full space-y-1 p-2">
              {files.map((file) => {
                const key = getFileKey(file);
                const status = state.fileStatuses.get(key) || "pending";
                return (
                  <div
                    key={key}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-md px-2 py-1 text-sm",
                      status === "uploading" && "bg-primary/5",
                      status === "completed" && "bg-green-50",
                      status === "error" && "bg-destructive/5",
                    )}
                  >
                    <FileStatusIcon status={status} isUploading={isUploading} />
                    <span
                      className="min-w-0 flex-1 overflow-hidden truncate"
                      title={file.name}
                    >
                      {file.name}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {formatFileSize(file.size)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter>
          {isUploading ? (
            <Button variant="destructive" onClick={onCancel}>
              Cancel Upload
            </Button>
          ) : (
            <Button onClick={onClose}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
